import { RefObject, useLayoutEffect } from 'react';
import type { TableRef } from 'antd/es/table';
import { clientToCssPixels } from '../utils/displaySize';

const CURRENT_DAY_HEADER = '.week-title-wrapper.current-day';
const HORIZONTAL_SCROLL_CONTAINER = '.ant-table-body, .ant-table-content';

/**
 * The week opens scrolled to its first day, which is usually several days
 * behind the one being filled in, so today's column starts off screen on a
 * task pane this narrow. Nudge the table just far enough to reveal it.
 *
 * This runs as a layout effect so the offset is applied before the browser
 * paints the table; on a plain effect the user watches it slide across.
 */
export const useScrollCurrentDayIntoView = (
  tableRef: RefObject<TableRef | null>,
  isTableRendered: boolean,
): void => {
  useLayoutEffect(() => {
    if (!isTableRendered) return;

    const table = tableRef.current?.nativeElement;
    const scrollContainer = table?.querySelector<HTMLElement>(HORIZONTAL_SCROLL_CONTAINER);
    const currentDayCell = table?.querySelector(CURRENT_DAY_HEADER)?.closest('th');
    if (!scrollContainer || !currentDayCell) return;

    const visibleRightEdge =
      clientToCssPixels(scrollContainer.getBoundingClientRect().left) + scrollContainer.clientWidth;
    const hiddenWidth =
      clientToCssPixels(currentDayCell.getBoundingClientRect().right) - visibleRightEdge;
    if (hiddenWidth <= 0) return;

    scrollContainer.scrollLeft += hiddenWidth;
  }, [tableRef, isTableRendered]);
};
